import {
    CorePosition,
    OnboardingAccountRequest,
    OnboardingAccountView,
    OnboardingFundingMethod,
    OnboardingTransferAssets,
    OnboardingTransferType,
    PersonOrg,
    PersonOrgRelation,
    PersonOrgRelationType,
    PersonOrgType,
    PortfolioBeneficiaries,
    PortfolioBeneficiary,
    RegistrationType,
} from '@ndvr/customer-api';

import { formatCurrency } from 'utils';

import { AllKeys, isEntity, isHundred, relationTo, toHundredths } from './onboarding-model';

export type OwnerShape = 'PERSON' | 'JOINT' | 'ENTITY';

export const ACCOUNT_FEATURES = [
    'checkwritingEnabled',
    'debitCardEnabled',
    'marginEnabled',
    'optionsEnabled',
    'altsEnabled',
    'liquidityLinesEnabled',
] as const;

type AccountFeature = (typeof ACCOUNT_FEATURES)[number];
export const FEATURE_LABELS: Record<AccountFeature, string> = {
    checkwritingEnabled: 'Checkwriting',
    debitCardEnabled: 'Debit card',
    marginEnabled: 'Margin',
    optionsEnabled: 'Options',
    altsEnabled: 'Alts',
    liquidityLinesEnabled: 'Liquidity Lines',
};
export type AccountFeatureValues = Pick<OnboardingAccountRequest, AccountFeature>;

/** Missing selections stay unspecified; false is an explicit No. */
export const toFeatureValues = (account: AccountFeatureValues) => ({
    checkwritingEnabled: account.checkwritingEnabled ?? undefined,
    debitCardEnabled: account.debitCardEnabled ?? undefined,
    marginEnabled: account.marginEnabled ?? undefined,
    optionsEnabled: account.optionsEnabled ?? undefined,
    altsEnabled: account.altsEnabled ?? undefined,
    liquidityLinesEnabled: account.liquidityLinesEnabled ?? undefined,
});

export const featuresFor = (type?: RegistrationType): AccountFeature[] => {
    const registration = registrationOf(type);
    return registration ? [...registration.features, 'altsEnabled', 'liquidityLinesEnabled'] : [];
};

/** Clear hidden selections when the registration changes, before they can be saved or restored accidentally. */
export const applicableFeatureValues = (values: AccountFeatureValues, type?: RegistrationType) => {
    const available = featuresFor(type);
    return toFeatureValues(
        Object.fromEntries(ACCOUNT_FEATURES.map((key) => [key, available.includes(key) ? values[key] : undefined])),
    );
};

/** The registrations available for onboarding; the backend works out each one's tax type and the account's title from it. */
export const REGISTRATIONS: {
    value: RegistrationType;
    label: string;
    owner: OwnerShape;
    /** For an entity account: the kinds of entity the backend lets own it. */
    entityTypes?: PersonOrgType[];
    /** Beneficiaries on it are a transfer-on-death designation. */
    transferOnDeath?: boolean;
    beneficiaries: boolean;
    features: AccountFeature[];
}[] = [
    {
        value: RegistrationType.INDIVIDUAL,
        label: 'Individual',
        owner: 'PERSON',
        transferOnDeath: true,
        beneficiaries: true,
        features: ['checkwritingEnabled', 'debitCardEnabled', 'marginEnabled', 'optionsEnabled'],
    },
    {
        value: RegistrationType.JOINT_WROS,
        label: 'Joint WROS',
        owner: 'JOINT',
        transferOnDeath: true,
        beneficiaries: true,
        features: ['checkwritingEnabled', 'debitCardEnabled', 'marginEnabled', 'optionsEnabled'],
    },
    {
        value: RegistrationType.JOINT_TIC,
        label: 'Joint TIC',
        owner: 'JOINT',
        transferOnDeath: true,
        beneficiaries: true,
        features: ['checkwritingEnabled', 'debitCardEnabled', 'marginEnabled', 'optionsEnabled'],
    },
    {
        value: RegistrationType.TRADITIONAL_IRA,
        label: 'Traditional IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.ROTH_IRA,
        label: 'Roth IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.ROLLOVER_IRA,
        label: 'Rollover IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.SEP_IRA,
        label: 'SEP IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.INHERITED_IRA,
        label: 'Inherited IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.INHERITED_ROTH_IRA,
        label: 'Inherited Roth IRA',
        owner: 'PERSON',
        beneficiaries: true,
        features: ['optionsEnabled'],
    },
    {
        value: RegistrationType.TRUST,
        label: 'Trust',
        owner: 'ENTITY',
        entityTypes: [PersonOrgType.TRUST],
        beneficiaries: false,
        features: ['checkwritingEnabled', 'marginEnabled', 'optionsEnabled'],
    },
    {
        value: RegistrationType.UGMA_UTMA,
        label: 'UGMA/UTMA',
        owner: 'PERSON',
        beneficiaries: false,
        features: ['checkwritingEnabled'],
    },
    {
        value: RegistrationType.NON_PROTOTYPE_RETIREMENT,
        label: 'Nonprototype Retirement',
        owner: 'ENTITY',
        entityTypes: [PersonOrgType.TRUST, PersonOrgType.ORGANIZATION, PersonOrgType.FOUNDATION],
        beneficiaries: false,
        features: [],
    },
    {
        value: RegistrationType.BUSINESS,
        label: 'Business',
        owner: 'ENTITY',
        entityTypes: [PersonOrgType.ORGANIZATION, PersonOrgType.FOUNDATION],
        beneficiaries: false,
        features: ['checkwritingEnabled', 'debitCardEnabled', 'marginEnabled', 'optionsEnabled'],
    },
    {
        value: RegistrationType.OTHER,
        label: 'Other / Manual',
        owner: 'PERSON',
        beneficiaries: false,
        features: [],
    },
];

export const registrationOf = (type?: RegistrationType) => REGISTRATIONS.find((r) => r.value === type);

export const FUNDING_METHOD_LABELS: Record<OnboardingFundingMethod, string> = {
    [OnboardingFundingMethod.ACATS]: 'ACATS transfer',
    [OnboardingFundingMethod.ROLLOVER]: 'Rollover',
    [OnboardingFundingMethod.CASH_DEPOSIT]: 'Cash deposit',
    [OnboardingFundingMethod.WIRE_TRANSFER]: 'Wire transfer',
    [OnboardingFundingMethod.CHECK_DEPOSIT]: 'Check deposit',
    [OnboardingFundingMethod.DTC_TRANSFER]: 'DTC transfer',
    [OnboardingFundingMethod.FIDELITY_JOURNAL]: 'Fidelity journal',
};

export const CORE_POSITIONS: { value: CorePosition; label: string }[] = [
    { value: CorePosition.FDIC_DEPOSIT_SWEEP, label: 'FDIC-insured deposit sweep' },
    { value: CorePosition.SPAXX, label: 'Fidelity Government Money Market (SPAXX)' },
    { value: CorePosition.FDRXX, label: 'Fidelity Government Cash Reserves (FDRXX)' },
    { value: CorePosition.FZFXX, label: 'Fidelity Treasury Money Market (FZFXX)' },
];

export const TRANSFER_TYPES: { value: OnboardingTransferType; label: string }[] = [
    { value: OnboardingTransferType.FULL, label: 'Full' },
    { value: OnboardingTransferType.PARTIAL, label: 'Partial' },
];

export const TRANSFER_ASSETS: { value: OnboardingTransferAssets; label: string }[] = [
    { value: OnboardingTransferAssets.IN_KIND, label: 'In kind' },
    { value: OnboardingTransferAssets.CASH, label: 'Cash' },
];

export interface AccountRow {
    account: OnboardingAccountView;
    /** The people who own it, the primary owner of a joint first, or the trust or business. */
    owners: string[];
}

export const toAccountRow = (account: OnboardingAccountView): AccountRow => ({
    account,
    owners: account.entityPersonOrgGUID ? [account.entityPersonOrgGUID] : account.ownerPersonOrgGUIDs,
});

/** The build records a missing statement value as 0. */
export const accountValue = (account: OnboardingAccountView) =>
    Number(account.value) > 0 ? formatCurrency(account.value) : '—';

/** The expected value as the card edits it, in dollars; empty while unknown (stored as 0). */
export const toValueInput = (account: OnboardingAccountView) =>
    Number(account.value) > 0 ? String(Number(account.value)) : '';

/**
 * Dollars as typed, "$12,500.50" too, in the form the request takes; empty is 0, the way the build records an unknown
 * value (the backend's `@Minimum` refuses a missing one). `undefined` when it is not an amount.
 */
export const toDollars = (input: string) => {
    const amount = input.replace(/[$,\s]/g, '');
    if (!amount) return '0';
    return /^\d+(\.\d{1,2})?$/.test(amount) ? amount : undefined;
};

/** The Fidelity title; an account still without an owner has none, so its registration stands in. */
export const accountTitle = (account: OnboardingAccountView) =>
    account.title || registrationOf(account.registrationType)?.label || 'New account';

export interface BeneficiaryRow {
    personOrgGUID: string;
    share: string;
    perStirpes: boolean;
}

export interface BeneficiaryValues {
    primary: BeneficiaryRow[];
    contingent: BeneficiaryRow[];
}

export const toBeneficiaryValues = (account: OnboardingAccountView): BeneficiaryValues => {
    const toRows = (beneficiaries: PortfolioBeneficiary[] = []) =>
        beneficiaries.map((beneficiary) => ({
            personOrgGUID: beneficiary.personOrgGUID,
            share: String(Number(beneficiary.ownershipPercentage)),
            perStirpes: !!beneficiary.perStirpesDesignationEnabled,
        }));
    return {
        primary: toRows(account.beneficiaries?.primaryBeneficiaries),
        contingent: toRows(account.beneficiaries?.contingentBeneficiaries),
    };
};

export const toPortfolioBeneficiaries = (values: BeneficiaryValues): PortfolioBeneficiaries => {
    const toBeneficiaries = (rows: BeneficiaryRow[]) =>
        rows.map((row) => ({
            personOrgGUID: row.personOrgGUID,
            ownershipPercentage: row.share,
            perStirpesDesignationEnabled: row.perStirpes,
        }));
    return {
        primaryBeneficiaries: toBeneficiaries(values.primary),
        contingentBeneficiaries: toBeneficiaries(values.contingent),
    };
};

/**
 * Why the account card cannot be saved, or `undefined` when it can. `beneficiaries` only when the registration takes
 * them; the backend cannot store a beneficiary without its person.
 */
export const saveBlocker = (
    registrationType: RegistrationType | undefined,
    value: string,
    beneficiaries?: BeneficiaryValues,
) => {
    if (!registrationType) return 'Select a registration type';
    if (toDollars(value) === undefined) return 'Enter the expected value as a dollar amount';
    if (beneficiaries) {
        const problems = [
            validateBeneficiaries(beneficiaries.primary),
            validateBeneficiaries(beneficiaries.contingent),
        ];
        return (
            problems.flatMap(({ rows }) => rows.map(({ person }) => person)).find(Boolean) ??
            problems.flatMap(({ rows }) => rows.map(({ share }) => share)).find(Boolean) ??
            problems.map(({ totalError }) => totalError).find(Boolean)
        );
    }
    return undefined;
};

/** Why the owners cannot be saved: the backend takes only as many owners as the registration has, two for a joint. */
export const ownersBlocker = (registrationType: RegistrationType | undefined, owners: string[]) => {
    const shape = registrationOf(registrationType)?.owner;
    if (!shape || new Set(owners.filter(Boolean)).size >= (shape === 'JOINT' ? 2 : 1)) return undefined;
    if (shape === 'JOINT') return 'Select both owners';
    return shape === 'ENTITY' ? 'Select the owning entity' : 'Select the owner';
};

export const sameBeneficiaries = (a: BeneficiaryValues, b: BeneficiaryValues) =>
    JSON.stringify(a) === JSON.stringify(b);

/** Values needed to save an account; a new account has no feature selections yet. */
export interface AccountRequestValues {
    registrationType: RegistrationType;
    owners: string[];
    expectedValue: string;
    contraFirm: string;
    contraAccountNumber: string;
    transfer: TransferValues;
    features?: AccountFeatureValues;
}

/**
 * The whole account request, as the endpoint replaces the account. The expected value is what staff typed, read from
 * a statement or heard on a call; one that is not an amount is sent as 0, so callers refuse it first.
 */
export const toAccountRequest = ({
    registrationType,
    owners,
    expectedValue,
    contraFirm,
    contraAccountNumber,
    transfer,
    features = {},
}: AccountRequestValues): OnboardingAccountRequest =>
    ({
        registrationType,
        ownerPersonOrgGUIDs: owners,
        value: toDollars(expectedValue) ?? '0',
        corePosition: transfer.corePosition || undefined,
        contraFirm: contraFirm.trim() || undefined,
        contraAccountNumber: contraAccountNumber.trim() || undefined,
        transferType: transfer.transferType || undefined,
        transferAssets: transfer.transferAssets || undefined,
        rolloverFrom: transfer.rolloverFrom.trim() || undefined,
        ...applicableFeatureValues(features, registrationType),
    }) satisfies AllKeys<OnboardingAccountRequest>;

/** Where the account's cash sits and how the money moves, as the card edits them; empty while not picked. */
export interface TransferValues {
    corePosition: CorePosition | '';
    transferType: OnboardingTransferType | '';
    transferAssets: OnboardingTransferAssets | '';
    rolloverFrom: string;
}

/** A new account's: nothing picked yet. */
export const EMPTY_TRANSFER: TransferValues = {
    corePosition: '',
    transferType: '',
    transferAssets: '',
    rolloverFrom: '',
};

export const toTransferValues = (account: OnboardingAccountView): TransferValues => ({
    corePosition: account.corePosition ?? '',
    transferType: account.transferType ?? '',
    transferAssets: account.transferAssets ?? '',
    rolloverFrom: account.rolloverFrom ?? '',
});

export interface AccountFormValues {
    registrationType?: RegistrationType;
    owners: string[];
    expectedValue: string;
    contraFirm: string;
    contraAccountNumber: string;
    transfer: TransferValues;
    features: AccountFeatureValues;
    beneficiaries: BeneficiaryValues;
}

/**
 * The values with another registration. Owners start again when it takes another kind of owner, since a trust cannot
 * own a joint or business account; answers for features it does not offer are dropped.
 */
export const withRegistration = (values: AccountFormValues, next: RegistrationType): AccountFormValues => {
    const shape = registrationOf(values.registrationType)?.owner ?? 'PERSON';
    return {
        ...values,
        registrationType: next,
        owners: registrationOf(next)?.owner !== shape || shape === 'ENTITY' ? [] : values.owners,
        features: applicableFeatureValues(values.features, next),
    };
};

/** What a registration change would clear: whether the owners go, and the labels of the feature answers dropped. */
export const clearedByRegistration = (before: AccountFormValues, after: AccountFormValues) => ({
    owners: after.owners.length < before.owners.length,
    features: ACCOUNT_FEATURES.filter(
        (feature) => before.features[feature] !== undefined && after.features[feature] === undefined,
    ).map((feature) => FEATURE_LABELS[feature]),
});

/** What the account card edits, as stored. */
export const toAccountFormValues = ({ account, owners }: AccountRow): AccountFormValues => ({
    registrationType: account.registrationType,
    owners,
    expectedValue: toValueInput(account),
    contraFirm: account.contraFirm ?? '',
    contraAccountNumber: account.contraAccountNumber ?? '',
    transfer: toTransferValues(account),
    features: toFeatureValues(account),
    beneficiaries: toBeneficiaryValues(account),
});

/** ACATS fields become required together once any transfer detail is entered. */
export const requiresTransferDetails = ({
    contraFirm,
    contraAccountNumber,
    transfer,
}: Pick<AccountFormValues, 'contraFirm' | 'contraAccountNumber' | 'transfer'>) =>
    !!(contraFirm.trim() || contraAccountNumber.trim() || transfer.transferType || transfer.transferAssets);

/** Count applicable editable fields; derived values and post-opening custody details do not block completion. */
export const countMissingAccountFields = ({
    registrationType,
    owners,
    expectedValue,
    contraFirm,
    contraAccountNumber,
    transfer,
    features,
    beneficiaries,
}: AccountFormValues) => {
    const registration = registrationOf(registrationType);
    const ownerCount = registration?.owner === 'JOINT' ? 2 : 1;
    const transferring = requiresTransferDetails({ contraFirm, contraAccountNumber, transfer });
    const checks = [
        !!registration,
        Number(toDollars(expectedValue)) > 0,
        !!transfer.corePosition,
        ...featuresFor(registrationType).map((key) => features[key] != null),
        ...(transferring
            ? [!!contraFirm.trim(), !!contraAccountNumber.trim(), !!transfer.transferType, !!transfer.transferAssets]
            : []),
        ...(registrationType === RegistrationType.ROLLOVER_IRA ? [!!transfer.rolloverFrom.trim()] : []),
    ];
    const missingOwners = Math.max(0, ownerCount - new Set(owners.filter(Boolean)).size);
    const missingBeneficiaries = registration?.beneficiaries
        ? validateBeneficiaries(beneficiaries.primary).missing + validateBeneficiaries(beneficiaries.contingent).missing
        : 0;
    return checks.filter((complete) => !complete).length + missingOwners + missingBeneficiaries;
};

/** A share must be a finite, positive percentage; totals are checked separately. */
export const isValidShare = ({ share }: BeneficiaryRow) =>
    !!share.trim() && Number.isFinite(Number(share)) && Number(share) > 0;

/** The shares' total, added in hundredths so decimal splits total exactly. */
export const shareTotal = (rows: BeneficiaryRow[]) =>
    rows.reduce((total, row) => total + toHundredths(row.share), 0) / 100;

/** One source of row and total problems for completion, saving and inline feedback. Empty lists are valid. */
export const validateBeneficiaries = (beneficiaries: BeneficiaryRow[]) => {
    const rows = beneficiaries.map((row) => ({
        person: row.personOrgGUID ? undefined : 'Select a person for each beneficiary, or remove the row',
        share: isValidShare(row) ? undefined : 'Enter a finite, positive share for each beneficiary',
    }));
    const total = shareTotal(beneficiaries);
    const totalError =
        beneficiaries.length > 0 && rows.every(({ share }) => !share) && !isHundred(total)
            ? `Shares total ${total}% — must equal 100%`
            : undefined;
    const missing =
        rows.reduce((count, row) => count + Number(!!row.person) + Number(!!row.share), 0) + Number(!!totalError);
    return { rows, total, totalError, missing };
};

/** Spouse / Non-spouse / Trust / Entity, derived from the beneficiary's type and any spouse relation to the owners. */
export const beneficiaryRelationship = (
    beneficiary: PersonOrg | undefined,
    owners: string[],
    relations: PersonOrgRelation[],
) => {
    if (!beneficiary) return '';
    if (beneficiary.personOrgType === PersonOrgType.TRUST) return 'Trust';
    if (isEntity(beneficiary)) return 'Entity';
    const isSpouse = owners.some(
        (owner) => relationTo(relations, owner, beneficiary.personOrgGUID) === PersonOrgRelationType.SPOUSE,
    );
    return isSpouse ? 'Spouse' : 'Non-spouse';
};
